import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface ConversationSummary {
	id: string;
	kind: 'team_support' | 'direct';
	last_message_at: string;
	last_message_preview: string | null;
	last_read_at: string | null;
	unread: boolean;
	other_user_id: string | null;
	other_username: string | null;
	other_avatar_url: string | null;
}

/**
 * Loads all conversations the signed-in user participates in (team + p2p),
 * sorted by most-recent message. Subscribes to realtime for new messages
 * and bumps `last_message_at` / `last_message_preview` / `unread` accordingly.
 */
export const useMyConversations = () => {
	const { user } = useAuth();
	const [conversations, setConversations] = useState<ConversationSummary[]>([]);
	const [loading, setLoading] = useState(false);

	const load = useCallback(async () => {
		if (!user) {
			setConversations([]);
			return;
		}
		setLoading(true);
		// H1: one server-side RPC replaces 3–4 round-trips (participants → others →
		// profiles → blocks). RPC scopes itself to auth.uid() and already filters
		// out conversations where either party blocked the other.
		const { data, error } = await (supabase as any).rpc('get_my_conversations');
		if (error) {
			console.error('[useMyConversations] load failed', error);
			setLoading(false);
			return;
		}
		const summaries: ConversationSummary[] = ((data ?? []) as any[]).map((r) => ({
			id: r.id,
			kind: r.kind,
			last_message_at: r.last_message_at,
			last_message_preview: r.last_message_preview ?? null,
			last_read_at: r.last_read_at,
			unread: !!r.unread,
			other_user_id: r.other_user_id ?? null,
			other_username: r.other_username ?? null,
			other_avatar_url: r.other_avatar_url ?? null,
		}));
		setConversations(summaries);
		setLoading(false);
	}, [user?.id]);

	useEffect(() => {
		load();
	}, [load]);

	// Keep latest load() accessible to the realtime callback without
	// re-subscribing every time its identity changes.
	const loadRef = useRef(load);
	useEffect(() => {
		loadRef.current = load;
	}, [load]);

	// Realtime: any new message in one of OUR conversations bumps it.
	// Re-subscribe only when the set of conversation IDs materially changes
	// (sorted-join key), not on every conversation reorder/unread toggle.
	const convIdsKey = conversations.map((c) => c.id).sort().join(',');
	useEffect(() => {
		if (!user) return;
		if (!convIdsKey) return; // nothing to listen for yet
		// Stable channel name + filter scoped to our conversations.
		// removeChannel in cleanup handles StrictMode double-mount correctly;
		// a Math.random() suffix would leak channels on every re-render cycle.
		const ids = convIdsKey.split(',');
		const channel = supabase
			.channel(`convs:${user.id}`)
			.on(
				'postgres_changes',
				{
					event: 'INSERT',
					schema: 'public',
					table: 'chat_messages',
					filter: `conversation_id=in.(${ids.join(',')})`,
				},
				(payload) => {
					const m = payload.new as { conversation_id: string; body: string; created_at: string; sender_id: string | null };
					setConversations((prev) => {
						const idx = prev.findIndex((c) => c.id === m.conversation_id);
						if (idx === -1) {
							// New conversation we don't know about yet → refetch
							loadRef.current();
							return prev;
						}
						const next = [...prev];
						const isMine = m.sender_id === user.id;
						next[idx] = {
							...next[idx],
							last_message_at: m.created_at,
							last_message_preview: m.body.slice(0, 140),
							unread: isMine ? next[idx].unread : true,
						};
						next.sort(
							(a, b) => new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
						);
						return next;
					});
				}
			)
			.subscribe();
		return () => {
			supabase.removeChannel(channel);
		};
	}, [user?.id, convIdsKey]);

	const markRead = useCallback(
		async (conversationId: string) => {
			if (!user) return;
			await supabase
				.from('chat_participants')
				.update({ last_read_at: new Date().toISOString() })
				.eq('conversation_id', conversationId)
				.eq('user_id', user.id);
			setConversations((prev) =>
				prev.map((c) => (c.id === conversationId ? { ...c, unread: false } : c))
			);
		},
		[user]
	);

	// L6: memoize unread count so consumers don't recompute on every render
	const unreadCount = useMemo(
		() => conversations.reduce((n, c) => (c.unread ? n + 1 : n), 0),
		[conversations]
	);

	return { conversations, loading, unreadCount, markRead, reload: load };
};
